'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getOrganization, updateOrganization, deleteLogo, getLogoFile } from '@/services/organizationService';
import type { Organization, UpdateOrganizationParams } from '@/types/organization';

interface OrganizationContextType {
  organization: Organization | null;
  organizationLogoPreview: string | null;
  isLoadingOrganization: boolean;
  isUpdatingOrganization: boolean;
  isDeletingLogo: boolean;
  setOrganization: (org: Organization | null) => void;
  setOrganizationLogoPreview: (preview: string | null) => void;
  refreshOrganization: () => Promise<void>;
  updateOrganizationData: (params: UpdateOrganizationParams) => Promise<Organization>;
  deleteOrganizationLogo: (logoId: string) => Promise<void>;
  loadLogoPreview: (logoUrl: string) => Promise<string | null>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizationLogoPreview, setOrganizationLogoPreview] = useState<string | null>(null);
  const [isLoadingOrganization, setIsLoadingOrganization] = useState(true);
  const [isUpdatingOrganization, setIsUpdatingOrganization] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);
  const { user } = useAuth();
  const isRefreshingRef = useRef(false);

  /**
   * Load logo preview from logo URL
   * Returns blob URL or null if failed
   */
  const loadLogoPreview = useCallback(async (logoUrl: string): Promise<string | null> => {
    try {
      // Extract logo ID from URL
      const logoIdMatch = logoUrl.match(/\/logos\/([^\/]+)$/);
      if (!logoIdMatch) {
        return null;
      }
      
      const logoId = logoIdMatch[1];
      
      // Use API service to fetch logo (bypasses CORS issues)
      const logoBlob = await getLogoFile(logoId);
      const blobUrl = URL.createObjectURL(logoBlob);
      
      // Cache logo in localStorage as base64 for persistence
      const reader = new FileReader();
      reader.readAsDataURL(logoBlob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        try {
          localStorage.setItem('ims_org_logo_cache', base64data);
          localStorage.setItem('ims_org_logo_url', logoUrl);
        } catch (e) {
          // Ignore if localStorage is full
          console.warn('Failed to cache logo:', e);
        }
      };
      
      return blobUrl;
    } catch (error) {
      console.error('Failed to load logo preview:', error);
      return null;
    }
  }, []);

  /**
   * Refresh organization data from API
   */
  const refreshOrganization = useCallback(async () => {
    // Prevent multiple simultaneous refreshes
    if (isRefreshingRef.current) {
      return;
    }

    if (!user) {
      setIsLoadingOrganization(false);
      return;
    }

    const organizationId = (user as any).tenant_id;

    if (!organizationId) {
      setIsLoadingOrganization(false);
      return;
    }

    try {
      isRefreshingRef.current = true;
      setIsLoadingOrganization(true);
      
      // Fetch complete organization data (includes name and logo_url)
      const orgData = await getOrganization(organizationId);
      setOrganization(orgData);
      
      // Revoke old blob URL to free memory
      if (organizationLogoPreview && organizationLogoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(organizationLogoPreview);
      }
      
      // If organization has a logo URL, fetch it as blob
      if (orgData.logo_url) {
        const blobUrl = await loadLogoPreview(orgData.logo_url);
        setOrganizationLogoPreview(blobUrl);
      } else {
        setOrganizationLogoPreview(null);
      }
    } catch (error) {
      console.error('Failed to refresh organization:', error);
      setOrganization(null);
      setOrganizationLogoPreview(null);
    } finally {
      setIsLoadingOrganization(false);
      isRefreshingRef.current = false;
    }
  }, [user, organizationLogoPreview, loadLogoPreview]);

  /**
   * Update organization data
   * Handles both name and logo updates
   */
  const updateOrganizationData = useCallback(async (params: UpdateOrganizationParams): Promise<Organization> => {
    if (!organization) {
      throw new Error('No organization loaded');
    }

    try {
      setIsUpdatingOrganization(true);
      
      const updatedOrg = await updateOrganization(organization.id, params);
      
      // Update context with new data
      setOrganization(updatedOrg);
      
      // If logo was updated, fetch the new logo preview
      if (updatedOrg.logo_url) {
        const blobUrl = await loadLogoPreview(updatedOrg.logo_url);
        setOrganizationLogoPreview(blobUrl);
      } else if (params.logo === undefined) {
        // If name was updated but logo wasn't changed, keep existing preview
        // Do nothing
      } else {
        // Logo was removed
        setOrganizationLogoPreview(null);
      }
      
      return updatedOrg;
    } catch (error) {
      console.error('Failed to update organization:', error);
      throw error;
    } finally {
      setIsUpdatingOrganization(false);
    }
  }, [organization, loadLogoPreview]);

  /**
   * Delete organization logo
   */
  const deleteOrganizationLogo = useCallback(async (logoId: string): Promise<void> => {
    try {
      setIsDeletingLogo(true);
      
      await deleteLogo(logoId);
      
      // Clear logo preview
      if (organizationLogoPreview && organizationLogoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(organizationLogoPreview);
      }
      setOrganizationLogoPreview(null);
      
      // Clear localStorage cache
      try {
        localStorage.removeItem('ims_org_logo_cache');
        localStorage.removeItem('ims_org_logo_url');
      } catch (e) {
        console.warn('Failed to clear logo cache:', e);
      }
      
      // Refresh organization to get updated data
      await refreshOrganization();
    } catch (error) {
      console.error('Failed to delete logo:', error);
      throw error;
    } finally {
      setIsDeletingLogo(false);
    }
  }, [organizationLogoPreview, refreshOrganization]);

  // Load cached logo on initial mount (before authentication)
  useEffect(() => {
    const loadCachedLogo = () => {
      try {
        const cachedLogo = localStorage.getItem('ims_org_logo_cache');
        if (cachedLogo) {
          setOrganizationLogoPreview(cachedLogo);
        }
      } catch (e) {
        console.warn('Failed to load cached logo:', e);
      }
    };
    
    loadCachedLogo();
  }, []);

  // Load organization on mount and when user changes
  useEffect(() => {
    refreshOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only re-run when user changes, not when refreshOrganization changes

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (organizationLogoPreview && organizationLogoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(organizationLogoPreview);
      }
    };
  }, [organizationLogoPreview]);

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        organizationLogoPreview,
        isLoadingOrganization,
        isUpdatingOrganization,
        isDeletingLogo,
        setOrganization,
        setOrganizationLogoPreview,
        refreshOrganization,
        updateOrganizationData,
        deleteOrganizationLogo,
        loadLogoPreview,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
